// src/components/BarraProfesional.tsx
//
// Barra flotante de abajo para el profesional, solo en el celular (oct-2026,
// pedido de Esteban). En el panel, casi todo quedaba lejos: la grilla de
// accesos está debajo del perfil, el link y las métricas, y en las otras
// pantallas había que volver al panel o abrir el menú. La barra deja a un
// toque lo de todos los días, con el calendario en el botón del centro, y
// "Más" abre el resto.
//
// Se monta una sola vez (App.tsx) y aparece solo con un profesional logueado,
// en /dashboard/profesional y /professional/*. Mientras se ve, el body lleva
// la clase `barra-pro-visible`: deja lugar abajo para que la barra no tape el
// final de la página y sube los botones flotantes (reglas en
// BarraProfesional.css). Mientras se escribe en un campo se esconde: en
// Android lo fijo abajo sube con el teclado y quedaría encima del formulario.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Users, CalendarDays, Settings, LayoutGrid, X, Plus, Clock, MapPin, ClipboardList,
  FileSignature, DollarSign, CreditCard, MessageCircle, BarChart3, Image, ListChecks, BadgeCheck, UserCog, Headphones,
} from 'lucide-react';
import { SOPORTE_WHATSAPP_URL } from './WhatsAppButton';
import './BarraProfesional.css';

interface Opcion {
  ruta: string;
  texto: string;
  icono: React.ReactNode;
  color: string; // fondo del ícono en la hoja "Más"
}

const leerUsuario = () => {
  try {
    if (localStorage.getItem('isLoggedIn') !== 'true') return null;
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

// Pantallas a pantalla completa, con sus propios botones abajo
const SIN_BARRA = ['/professional/clinical-records/template'];

const Boton: React.FC<{ texto: string; icono: React.ReactNode; activo: boolean; onClick: () => void }> = ({ texto, icono, activo, onClick }) => (
  <button type="button" className={`bp-item${activo ? ' is-activo' : ''}`} aria-current={activo ? 'page' : undefined} onClick={onClick}>
    {icono}
    <span>{texto}</span>
  </button>
);

const BarraProfesional: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState(leerUsuario);
  const [hojaAbierta, setHojaAbierta] = useState(false);
  const [escribiendo, setEscribiendo] = useState(false);
  // Cada toque al calendario suma uno: la onda del botón se vuelve a montar
  // con otra clave y su animación arranca de nuevo
  const [ondas, setOndas] = useState(0);

  // El usuario puede cambiar (login, logout) sin que se remonte la app
  useEffect(() => {
    setUsuario(leerUsuario());
    setHojaAbierta(false);
  }, [pathname]);

  const esProfesional = !!usuario?.es_profesional && !usuario?.isAssistant && !usuario?.is_assistant;
  const enZonaProfesional = pathname.startsWith('/dashboard/profesional') || pathname.startsWith('/professional/');
  const visible = esProfesional && enZonaProfesional && !SIN_BARRA.some((r) => pathname.startsWith(r));

  useEffect(() => {
    if (!visible) return;
    document.body.classList.add('barra-pro-visible');
    return () => document.body.classList.remove('barra-pro-visible');
  }, [visible]);

  // Esconderla mientras se escribe
  useEffect(() => {
    if (!visible) return;
    const esCampo = (e: EventTarget | null) => {
      const el = e as HTMLElement | null;
      if (!el || !el.tagName) return false;
      if (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
      if (el.tagName !== 'INPUT') return false;
      const tipo = (el as HTMLInputElement).type;
      return !['checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file'].includes(tipo);
    };
    let t: ReturnType<typeof setTimeout> | undefined;
    const entra = (e: FocusEvent) => { if (esCampo(e.target)) { clearTimeout(t); setEscribiendo(true); } };
    // Con demora: al pasar de un campo a otro no tiene que asomar y volver a irse
    const sale = (e: FocusEvent) => { if (esCampo(e.target)) { clearTimeout(t); t = setTimeout(() => setEscribiendo(false), 150); } };
    document.addEventListener('focusin', entra);
    document.addEventListener('focusout', sale);
    return () => { clearTimeout(t); document.removeEventListener('focusin', entra); document.removeEventListener('focusout', sale); };
  }, [visible]);

  useEffect(() => {
    if (!hojaAbierta) return;
    document.body.classList.add('barra-pro-hoja');
    return () => document.body.classList.remove('barra-pro-hoja');
  }, [hojaAbierta]);

  useEffect(() => {
    if (!hojaAbierta) return;
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setHojaAbierta(false); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [hojaAbierta]);

  if (!visible) return null;

  const salud = usuario?.professional_type !== 'belleza';
  const rutaPacientes = salud ? '/professional/patients' : '/professional/clients';

  const opciones: Opcion[] = [
    { ruta: '/professional/new-appointment', texto: salud ? 'Nueva consulta' : 'Nuevo turno', icono: <Plus />, color: 'bg-gradient-to-br from-blue-500 to-green-500' },
    { ruta: '/professional/schedule-config', texto: 'Horarios', icono: <Clock />, color: 'bg-blue-500' },
    { ruta: '/professional/locations-config', texto: 'Sedes', icono: <MapPin />, color: 'bg-purple-500' },
    ...(salud
      ? [
          { ruta: '/professional/clinical-records', texto: 'Historias clínicas', icono: <ClipboardList />, color: 'bg-teal-500' },
          { ruta: '/professional/prescriptions', texto: 'Recetas', icono: <FileSignature />, color: 'bg-emerald-500' },
        ]
      : []),
    { ruta: '/professional/payment-config', texto: 'Pagos', icono: <DollarSign />, color: 'bg-teal-500' },
    { ruta: '/professional/payment-verification', texto: 'Transferencias', icono: <CreditCard />, color: 'bg-amber-500' },
    { ruta: '/professional/notification-config', texto: 'WhatsApp', icono: <MessageCircle />, color: 'bg-green-500' },
    { ruta: '/professional/reports', texto: 'Reportes', icono: <BarChart3 />, color: 'bg-indigo-500' },
    { ruta: '/professional/portfolio', texto: 'Fotos', icono: <Image />, color: 'bg-sky-500' },
    { ruta: '/professional/booking-fields-config', texto: 'Datos del turno', icono: <ListChecks />, color: 'bg-orange-500' },
    // "Mi perfil" solo en belleza: en salud ya está "Editar Perfil" en el panel, y
    // con él quedaba una tarjeta sola en la última fila (13 en una grilla de 3)
    ...(salud ? [] : [{ ruta: '/professional/profile-config', texto: 'Mi perfil', icono: <UserCog />, color: 'bg-slate-500' }]),
    { ruta: '/professional/subscription', texto: 'Suscripción', icono: <BadgeCheck />, color: 'bg-violet-500' },
  ];

  const esta = (ruta: string) => pathname === ruta || pathname.startsWith(ruta + '/');
  const enInicio = pathname.startsWith('/dashboard/profesional');
  const enMas = hojaAbierta || opciones.some((o) => esta(o.ruta));

  const ir = (ruta: string) => {
    setHojaAbierta(false);
    if (ruta === '/dashboard/profesional' && enInicio) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(ruta);
  };

  return (
    <>
      {hojaAbierta && (
        <>
          <div className="bp-fondo" onClick={() => setHojaAbierta(false)} aria-hidden="true" />
          <div className="bp-hoja" role="dialog" aria-label="Más opciones">
            <div className="bp-hoja-titulo">
              <span>Más opciones</span>
              <button type="button" onClick={() => setHojaAbierta(false)} aria-label="Cerrar">
                <X />
              </button>
            </div>
            <div className="bp-hoja-grilla">
              {opciones.map((o) => (
                <button key={o.ruta} type="button" className={`bp-opcion${esta(o.ruta) ? ' is-activa' : ''}`} onClick={() => ir(o.ruta)}>
                  <span className={`bp-opcion-icono ${o.color}`}>{o.icono}</span>
                  <span className="bp-opcion-texto">{o.texto}</span>
                </button>
              ))}
            </div>
            {/* Soporte: en el celular del profesional, en vez del botón verde
                flotante (que tapaba el panel); ancho entero, para que se
                distinga de las secciones */}
            <a className="bp-soporte" href={SOPORTE_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => setHojaAbierta(false)}>
              <span className="bp-soporte-icono"><Headphones /></span>
              <span>
                <span className="bp-soporte-titulo">Soporte</span>
                <span className="bp-soporte-texto">Escribinos por WhatsApp</span>
              </span>
            </a>
          </div>
        </>
      )}

      <nav className={`bp-barra${escribiendo && !hojaAbierta ? ' is-oculta' : ''}`} aria-label="Accesos del profesional">
        <Boton onClick={() => ir('/dashboard/profesional')} texto="Inicio" icono={<Home />} activo={enInicio && !hojaAbierta} />
        <Boton onClick={() => ir(rutaPacientes)} texto={salud ? 'Pacientes' : 'Clientes'} icono={<Users />} activo={esta(rutaPacientes) && !hojaAbierta} />
        <div className="bp-centro">
          <button
            type="button"
            className={`bp-centro-boton${esta('/professional/calendar') ? ' is-activo' : ''}`}
            onClick={() => {
              setOndas((n) => n + 1);
              ir('/professional/calendar');
            }}
            aria-label="Calendario"
            aria-current={esta('/professional/calendar') ? 'page' : undefined}
          >
            <CalendarDays />
            {ondas > 0 && <span key={ondas} className="bp-onda" aria-hidden="true" />}
          </button>
          <span className={`bp-centro-texto${esta('/professional/calendar') ? ' is-activo' : ''}`}>Calendario</span>
        </div>
        <Boton onClick={() => ir('/professional/services-config')} texto="Servicios" icono={<Settings />} activo={esta('/professional/services-config') && !hojaAbierta} />
        <Boton texto="Más" icono={hojaAbierta ? <X /> : <LayoutGrid />} activo={enMas} onClick={() => setHojaAbierta((v) => !v)} />
      </nav>
    </>
  );
};

export default BarraProfesional;
